export const defaultLogoOptions = {
  zoom: 100,
  background: '#FFFFFF',
  filter: 'original',
  wordmark: false,
};

export const getLogoBounds = (width, height, options, size = 512) => {
  // At 100%, keep every corner inside the circular avatars used throughout Questri.
  const zoom = options.zoom / 100;
  if (!options.wordmark) {
    const scale = ((size - 4) * 0.9 * zoom) / Math.hypot(width, height);
    return {
      x: (size - width * scale) / 2,
      y: (size - height * scale) / 2,
      width: width * scale,
      height: height * scale,
    };
  }
  const innerSize = (size / Math.SQRT2) * 0.9;
  const availableHeight = innerSize * 0.72;
  const scale = Math.min(innerSize / width, availableHeight / height) * zoom;
  return {
    x: (size - width * scale) / 2,
    y: (size - innerSize) / 2 + (availableHeight - height * scale) / 2,
    width: width * scale,
    height: height * scale,
  };
};

const applyFilter = (context, bounds, filter) => {
  if (filter === 'original') return;
  // Pixel processing also works in browsers without CanvasRenderingContext2D.filter.
  const x = Math.max(0, Math.floor(bounds.x));
  const y = Math.max(0, Math.floor(bounds.y));
  const width = Math.min(context.canvas.width, Math.ceil(bounds.x + bounds.width)) - x;
  const height = Math.min(context.canvas.height, Math.ceil(bounds.y + bounds.height)) - y;
  const pixels = context.getImageData(x, y, width, height);
  const { data } = pixels;
  for (let index = 0; index < data.length; index += 4) {
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];
    if (filter === 'grayscale') {
      const gray = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
      data[index] = gray;
      data[index + 1] = gray;
      data[index + 2] = gray;
    } else {
      data[index] = 0.75 * red + 0.25 * (0.393 * red + 0.769 * green + 0.189 * blue);
      data[index + 1] = 0.75 * green + 0.25 * (0.349 * red + 0.686 * green + 0.168 * blue);
      data[index + 2] = 0.75 * blue + 0.25 * (0.272 * red + 0.534 * green + 0.131 * blue);
    }
  }
  context.putImageData(pixels, x, y);
};

export const renderLogo = (image, options) => {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  const bounds = getLogoBounds(image.naturalWidth, image.naturalHeight, options, size);
  const innerSize = (size / Math.SQRT2) * 0.9;
  context.save();
  if (options.wordmark) {
    // Reserve space for the label even when the logo is enlarged.
    context.beginPath();
    context.rect((size - innerSize) / 2, (size - innerSize) / 2, innerSize, innerSize * 0.72);
    context.clip();
  }
  context.drawImage(image, bounds.x, bounds.y, bounds.width, bounds.height);
  context.restore();
  applyFilter(context, bounds, options.filter);
  context.globalCompositeOperation = 'destination-over';
  context.fillStyle = options.background;
  context.fillRect(0, 0, size, size);
  context.globalCompositeOperation = 'source-over';
  if (options.wordmark) {
    const rgb = options.background.match(/[a-f\d]{2}/gi).map((part) => parseInt(part, 16));
    const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
    context.fillStyle = brightness > 150
      ? '#202520'
      : '#FFFFFF';
    context.font = '600 34px Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText('Questri', size / 2, (size - innerSize) / 2 + innerSize * 0.87);
  }
  return canvas.toDataURL('image/png');
};

export const loadLogo = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(url);
    if (!image.naturalWidth || !image.naturalHeight) {
      reject(new Error('Invalid image'));
    } else {
      resolve(image);
    }
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Invalid image'));
  };
  image.src = url;
});
